/**
 * Contract Draft Dashboard Redux Slice
 * 
 * Manages state for the Contract Draft Dashboard including:
 * - Table data with pagination
 * - Dashboard statistics
 * - Filters and sorting
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ContractRequestRow,
  ContractRequestListResponse,
  ContractDraftStatsResponse,
  AssignableCsUser,
} from '../../../services/contractReview.service';

export interface ActiveJob {
  requestId: string;
  projectName?: string | null;
  type: 'DRAFTING' | 'INTAKE' | 'REVIEW';
  startedAt: number;
}

const ACTIVE_JOBS_STORAGE_KEY = 'orbit_active_draft_jobs';

export const loadActiveJobsFromStorage = (): Record<string, ActiveJob> => {
  try {
    const saved = localStorage.getItem(ACTIVE_JOBS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

export const saveActiveJobsToStorage = (jobs: Record<string, ActiveJob>) => {
  try {
    localStorage.setItem(ACTIVE_JOBS_STORAGE_KEY, JSON.stringify(jobs));
  } catch {}
};

/**
 * State shape for Contract Draft Dashboard
 */
export interface ContractDraftDashboardState {
  tableData: {
    results: ContractRequestRow[];
    count: number;
    next: string | null;
    previous: string | null;
    loading: boolean;
    error: string | null;
  };
  stats: {
    data: ContractDraftStatsResponse | null;
    loading: boolean;
    error: string | null;
  };
  filters: {
    status: string[];
    contract_type_id: string | null;
    assigned_cs_id: string | null;
    assigned_cs_label: string | null;
    date_from: string | null;
    date_to: string | null;
    search: string;
  };
  pagination: {
    page: number;
    page_size: number;
  };
  sort: {
    field: string;
    order: 'asc' | 'desc';
  };
  assignableCsUsers: {
    data: AssignableCsUser[];
    loading: boolean;
    error: string | null;
  };
  lastFetch: number | null;
  draftStatusChangedAt: number | null;
  selectedVerticalId: string | null;
  activeJobs: Record<string, ActiveJob>;
}

/**
 * Initial state
 */
const initialState: ContractDraftDashboardState = {
  tableData: {
    results: [],
    count: 0,
    next: null,
    previous: null,
    loading: false,
    error: null,
  },
  stats: {
    data: null,
    loading: false,
    error: null,
  },
  filters: {
    status: [],
    contract_type_id: null,
    assigned_cs_id: null,
    assigned_cs_label: null,
    date_from: null,
    date_to: null,
    search: '',
  },
  pagination: {
    page: 1,
    page_size: 10,
  },
  sort: {
    field: 'created_at',
    order: 'desc',
  },
  assignableCsUsers: {
    data: [],
    loading: false,
    error: null,
  },
  lastFetch: null,
  draftStatusChangedAt: null,
  selectedVerticalId: null,
  activeJobs: loadActiveJobsFromStorage(),
};

/**
 * Contract Draft Dashboard Slice
 */
const contractDraftDashboardSlice = createSlice({
  name: 'contractDraftDashboard',
  initialState,
  reducers: {
    // Table Data Actions
    setTableLoading: (state, action: PayloadAction<boolean>) => {
      state.tableData.loading = action.payload;
    },
    setTableData: (state, action: PayloadAction<ContractRequestListResponse>) => {
      state.tableData.results = action.payload?.results || [];
      state.tableData.count = action.payload?.count || 0;
      state.tableData.next = action.payload?.next || null;
      state.tableData.previous = action.payload?.previous || null;
      state.tableData.loading = false;
      state.tableData.error = null;
      state.lastFetch = Date.now();
    },
    setTableError: (state, action: PayloadAction<string>) => {
      state.tableData.error = action.payload;
      state.tableData.loading = false;
    },
    
    // Stats Actions
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.stats.loading = action.payload;
    },
    setStatsData: (state, action: PayloadAction<ContractDraftStatsResponse>) => {
      state.stats.data = action.payload || null;
      state.stats.loading = false;
      state.stats.error = null;
    },
    setStatsError: (state, action: PayloadAction<string>) => {
      state.stats.error = action.payload;
      state.stats.loading = false;
    },
    
    // Filter Actions
    setStatusFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.status = action.payload;
      state.pagination.page = 1; // Reset to first page on filter change
    },
    setContractTypeFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.contract_type_id = action.payload;
      state.pagination.page = 1;
    },
    setAssignedCsFilter: (state, action: PayloadAction<{ id: string | null; label: string | null }>) => {
      state.filters.assigned_cs_id = action.payload.id;
      state.filters.assigned_cs_label = action.payload.label;
      state.pagination.page = 1;
    },
    setDateRangeFilter: (
      state,
      action: PayloadAction<{ date_from: string | null; date_to: string | null }>
    ) => {
      state.filters.date_from = action.payload.date_from;
      state.filters.date_to = action.payload.date_to;
      state.pagination.page = 1;
    },
    setSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
    },
    clearAllFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    
    // Pagination Actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.page_size = action.payload;
      state.pagination.page = 1; // Reset to first page on page size change
    },
    
    // Sorting Actions
    setSort: (
      state,
      action: PayloadAction<{ field: string; order: 'asc' | 'desc' }>
    ) => {
      state.sort = action.payload;
      state.pagination.page = 1; // Reset to first page on sort change
    },
    
    // Update Single Row (for optimistic UI updates)
    updateTableRow: (state, action: PayloadAction<ContractRequestRow>) => {
      const index = state.tableData.results.findIndex(
        (row) => row.request_id === action.payload.request_id
      );
      if (index !== -1) {
        state.tableData.results[index] = action.payload;
      }
    },
    
    // Trigger dashboard refresh when a draft status changes READY_FOR_DRAFTING → DRAFT_GENERATED
    triggerStatusRefresh: (state) => {
      state.draftStatusChangedAt = Date.now();
    },

    // Assignable CS Users Actions
    setAssignableCsUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.assignableCsUsers.loading = action.payload;
    },
    setAssignableCsUsersData: (state, action: PayloadAction<AssignableCsUser[]>) => {
      state.assignableCsUsers.data = action.payload;
      state.assignableCsUsers.loading = false;
      state.assignableCsUsers.error = null;
    },
    setAssignableCsUsersError: (state, action: PayloadAction<string>) => {
      state.assignableCsUsers.error = action.payload;
      state.assignableCsUsers.loading = false;
    },

    // Persist selected vertical so other pages can reuse it
    setDashboardSelectedVerticalId: (state, action: PayloadAction<string | null>) => {
      state.selectedVerticalId = action.payload;
    },

    // Active background jobs actions (for cross-page notifications)
    addActiveJob: (state, action: PayloadAction<ActiveJob>) => {
      state.activeJobs[action.payload.requestId] = action.payload;
      saveActiveJobsToStorage(state.activeJobs);
    },
    removeActiveJob: (state, action: PayloadAction<string>) => {
      delete state.activeJobs[action.payload];
      saveActiveJobsToStorage(state.activeJobs);
    },
    syncActiveJobs: (state) => {
      state.activeJobs = loadActiveJobsFromStorage();
    },

    // Reset State
    resetDashboard: () => initialState,
  },
});

export const {
  setTableLoading,
  setTableData,
  setTableError,
  setStatsLoading,
  setStatsData,
  setStatsError,
  setStatusFilter,
  setContractTypeFilter,
  setAssignedCsFilter,
  setDateRangeFilter,
  setSearchFilter,
  clearAllFilters,
  setPage,
  setPageSize,
  setSort,
  updateTableRow,
  triggerStatusRefresh,
  setAssignableCsUsersLoading,
  setAssignableCsUsersData,
  setAssignableCsUsersError,
  setDashboardSelectedVerticalId,
  addActiveJob,
  removeActiveJob,
  syncActiveJobs,
  resetDashboard,
} = contractDraftDashboardSlice.actions;

export default contractDraftDashboardSlice.reducer;
